import { useState, useEffect, useMemo } from 'react';
import {
  Card, Row, Col, Statistic, Select, Button, Spin, message, Tag, Alert, Table,
} from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { vppQuotation } from '../../../services/api';

export default function VppQuotationPage() {
  const [loading, setLoading] = useState(true);
  const [marketType, setMarketType] = useState('日前市场');
  const [riskLevel, setRiskLevel] = useState('中');
  const [quotation, setQuotation] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [strategies, setStrategies] = useState([]);

  const load = async () => {
    try {
      const [qRes, bRes, sRes] = await Promise.all([
        vppQuotation.aiGenerate({ market_type: marketType, risk_level: riskLevel }),
        vppQuotation.backtest({ strategy: 'AI智能报价', months: 3 }),
        vppQuotation.list(),
      ]);
      setQuotation(qRes.data);
      setBacktest(bRes.data);
      setStrategies(sRes.data || []);
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [marketType, riskLevel]);

  const curveOption = useMemo(() => {
    if (!quotation?.curve) return {};
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['AI报价', '负荷预测', '申报容量'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: quotation.curve.map((c) => c.time_label) },
      yAxis: [
        { type: 'value', name: '元/kWh', position: 'left' },
        { type: 'value', name: 'kW', position: 'right' },
      ],
      series: [
        { name: 'AI报价', type: 'line', smooth: true, data: quotation.curve.map((c) => c.recommended_price), itemStyle: { color: '#fa8c16' } },
        { name: '负荷预测', type: 'bar', yAxisIndex: 1, data: quotation.curve.map((c) => c.load_forecast_kw), itemStyle: { color: 'rgba(24,144,255,0.35)' } },
        { name: '申报容量', type: 'line', yAxisIndex: 1, smooth: true, data: quotation.curve.map((c) => c.bid_capacity_kw), itemStyle: { color: '#52c41a' } },
      ],
    };
  }, [quotation]);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const summary = quotation?.summary || {};

  return (
    <div>
      <Alert
        type="info"
        showIcon
        message="VPP AI 智能报价"
        description="基于负荷预测、峰谷价差与风险约束，自动生成 24h 报价曲线，支持策略回测与出清概率评估。"
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="均价报价" value={summary.avg_price} suffix="元/kWh" precision={3} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="预估收益" value={summary.expected_revenue_yuan} prefix="¥" /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="出清概率" value={summary.clearing_probability} suffix="%" precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="聚合容量" value={quotation?.capacity?.total_kw} suffix="kW" /></Card></Col>
      </Row>

      <Card title="AI 报价参数" style={{ marginTop: 16 }} size="small">
        <Row gutter={16}>
          <Col>
            市场类型：
            <Select value={marketType} style={{ width: 140, marginLeft: 8 }} onChange={setMarketType}>
              {['日前市场', '实时市场', '辅助服务', '需求响应'].map((m) => <Select.Option key={m} value={m}>{m}</Select.Option>)}
            </Select>
          </Col>
          <Col>
            风险等级：
            <Select value={riskLevel} style={{ width: 100, marginLeft: 8 }} onChange={setRiskLevel}>
              {['低', '中', '高'].map((r) => <Select.Option key={r} value={r}>{r}</Select.Option>)}
            </Select>
          </Col>
          <Col><Button type="primary" onClick={load}>重新生成</Button></Col>
        </Row>
        <div style={{ marginTop: 12, color: '#666', fontSize: 13 }}>{summary.strategy_note}</div>
        <Tag style={{ marginTop: 8 }}>{summary.ai_model}</Tag>
      </Card>

      <Card title="24h AI 报价曲线" style={{ marginTop: 16 }} size="small">
        <ReactEChartsCore option={curveOption} style={{ height: 320 }} />
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="策略列表" size="small">
            <Table
              size="small"
              pagination={false}
              dataSource={strategies.map((s) => ({ ...s, key: s.id }))}
              columns={[
                { title: '策略', dataIndex: 'name' },
                { title: '市场', dataIndex: 'market' },
                { title: '报价', dataIndex: 'price', render: (v) => `${v} 元` },
                { title: 'AI', dataIndex: 'ai_enabled', render: (v) => v ? <Tag color="blue">AI</Tag> : '-' },
              ]}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="策略回测" size="small">
            {backtest && (
              <>
                <Row gutter={8}>
                  <Col span={12}><Statistic title="总收益" value={backtest.total_revenue} prefix="¥" /></Col>
                  <Col span={12}><Statistic title="胜率" value={backtest.win_rate} suffix="%" /></Col>
                </Row>
                <div style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
                  AI vs 人工收益提升 <Tag color="green">+{backtest.ai_vs_manual?.uplift_pct}%</Tag>
                </div>
                <ul style={{ marginTop: 12, paddingLeft: 18, fontSize: 13, color: '#666' }}>
                  {(backtest.ai_insights || []).map((t) => <li key={t}>{t}</li>)}
                </ul>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
